import './calendar.scss';
import { DateTime, Info, Interval } from 'luxon';
import { useState } from 'react';
import classNames from 'classnames';
import { Typography } from '../../typography/typography';
import { Button } from '../../form/button';

export const Calendar = ({
  value,
  disabledDaysOfWeek = [],
  disabledIntervals = [],
  highlightedIntervals = [],
  showClearButton = false,
  onChange,
}: {
  value?: DateTime;
  disabledDaysOfWeek?: number[];
  disabledIntervals?: Interval[];
  highlightedIntervals?: { intervals: Interval; color: string }[];
  showClearButton?: boolean;
  onChange?: (d?: DateTime) => void;
}) => {
  const [startOfMonth, setStartOfMonth] = useState<DateTime>(
    (value?.isValid ? value : DateTime.now()).startOf('month'),
  );

  const getWeekdayNames = () => {
    const days = Info.weekdays('short');
    const first = days.pop();
    return [first, ...days];
  };

  const headers = getWeekdayNames();
  const parts = startOfMonth.toLocaleParts(DateTime.DATETIME_FULL);
  const month = parts.find((p) => p.type === 'month')?.value;
  const year = parts.find((p) => p.type === 'year')?.value;

  const days: (DateTime | undefined)[] = [];
  let currentDay = startOfMonth;
  let weekday = 6;

  while (currentDay.month === startOfMonth.month) {
    if (weekday + 1 !== currentDay.weekday) {
      days.push(undefined);
    } else {
      days.push(currentDay);
      currentDay = currentDay.plus({ days: 1 });
    }
    weekday = (weekday + 1) % 7;
  }

  return (
    <div className="quipli-calendar">
      <div className="calendar-top">
        <i className="fa-solid fa-angle-left" onClick={() => setStartOfMonth(startOfMonth.minus({ months: 1 }))} />
        <Typography type="text-sm"> {`${month} ${year}`}</Typography>
        <i className="fa fa-angle-right" onClick={() => setStartOfMonth(startOfMonth.plus({ months: 1 }))} />
      </div>
      <div className="calendar-month">
        {headers.map((h) => (
          <div key={h} className="calendar-day calendar-dow">
            <Typography type="text-xs"> {h}</Typography>
          </div>
        ))}
        {days.map((d, i) => {
          const empty = !d;
          const disabled =
            d &&
            (disabledIntervals.find(
              (i) =>
                i.contains(d) &&
                i.end.startOf('day').diff(d).milliseconds !== 0 &&
                i.start.startOf('day').diff(d).milliseconds !== 0,
            ) ||
              disabledDaysOfWeek.find((dow) => d.weekday === dow));
          const selected = d && value && Math.ceil(d.diff(value, 'days').days) === 0;
          const highlighted = d && highlightedIntervals.find((i) => i.intervals.contains(d));
          return (
            <div
              key={i}
              onClick={() =>
                !disabled &&
                d &&
                onChange &&
                onChange(
                  d.set({
                    hour: value?.isValid ? value?.hour : undefined,
                    minute: value?.isValid ? value?.minute : undefined,
                  }),
                )
              }
              className={classNames('calendar-day', { empty, disabled, selected })}
              style={highlighted && { backgroundColor: highlighted.color }}
            >
              <Typography type="text-sm"> {d?.day}</Typography>
            </div>
          );
        })}
      </div>
      {showClearButton && (
        <div className="flex items-center justify-center">
          <Button className="button-yellow" onClick={() => onChange && onChange(undefined)}>
            Clear
          </Button>
        </div>
      )}
    </div>
  );
};
